import rest from '@/lib/rest';
import { useUserStore } from '@/stores/user';
import { defineStore } from 'pinia';
import posthog from 'posthog-js';
import { ref } from 'vue';

export type SignUpOptions = {
  role?: string;
  /**
   * When true, the REST handler schedules `stripe.setupTrial` (creates the
   * Stripe customer + trial subscription) as part of signup side effects —
   * matching the dabble-writer 2.5 flow. Otherwise no Stripe state is
   * provisioned on signup.
   */
  trial?: boolean;
};

export const useAuthStore = defineStore('auth', () => {
  const loading = ref(false);
  const error = ref<string | null>(null);
  const signingIn = ref<boolean>(false);
  const signingUp = ref<boolean>(false);

  async function signUp(name: string, email: string, password: string, options?: SignUpOptions) {
    if (!signingUp.value) {
      signingUp.value = true;
      loading.value = true;
      try {
        // /auth/user reads `req.content.metadata`. `trial: true` triggers
        // the backend `stripe.setupTrial` task (creates Stripe customer +
        // trial atomically with signup, mirroring dabble-writer 2.5).
        // `role` flows through to customer.io / intercom identify.
        const metadata: Record<string, string | boolean> = {};
        if (options?.trial === true) {
          metadata.trial = true;
        }
        if (options?.role) {
          metadata.role = options.role;
        }
        const body: {
          name: string;
          email: string;
          password: string;
          metadata?: Record<string, string | boolean>;
        } = { name, email, password };
        if (Object.keys(metadata).length > 0) {
          body.metadata = metadata;
        }

        await rest.post(`auth/user`).send(body);

        const loginResponse = await login(email, password);

        return loginResponse;
      } catch (e: any) {
        error.value = e.message;
        throw e;
      } finally {
        signingUp.value = false;
        loading.value = false;
      }
    }
    return null;
  }

  async function login(email: string, password: string) {
    if (signingIn.value) {
      return null;
    }
    signingIn.value = true;
    /** Avoid clearing `loading` while `signUp` is running — it calls `login()` internally. */
    const manageLoading = !signingUp.value;
    if (manageLoading) {
      loading.value = true;
    }
    error.value = null;
    try {
      const response = await rest.post(`auth/signIn`).credentials('include').send({
        email,
        password,
      });
      if (response?.user?.uid) {
        localStorage.setItem('uid', response.user.uid);
        sessionStorage.setItem('uid', response.user.uid);
        localStorage.setItem('hasLoggedIn', '1');
        sessionStorage.setItem('hasLoggedIn', '1');
      }
      return response;
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      signingIn.value = false;
      if (manageLoading) {
        loading.value = false;
      }
    }
  }

  async function logout() {
    const userStore = useUserStore();
    const uid = userStore.user.uid;
    if (uid) {
      await rest.delete('auth/signIn').header('x-uid', uid).credentials('include').send();
    }
    sessionStorage.removeItem('impersonate');
    sessionStorage.removeItem('uid');
    localStorage.removeItem('uid');
    userStore.clear();
    // Reset PostHog identity synchronously before the reload aborts in-flight
    // requests, so the next user (or anonymous session) starts with a fresh
    // distinct_id instead of being merged with this one.
    posthog.reset();
    // The sealed `dbl2` cookie may still hold other signed-in accounts.
    // Reload so accounts.init() re-derives the active uid from the cookie:
    // a remaining account becomes active, or the router falls through to
    // /auth/login when no accounts remain.
    location.assign('/');
  }

  return {
    loading,
    signingIn,
    signingUp,
    error,
    login,
    signUp,
    logout,
  };
});
